import { Component, ViewChild, Output, EventEmitter, OnInit } from '@angular/core';
import { ModalComponent } from '../../components/modal/modal.component';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '../../components/toast/toast.service';
import { TagService } from '../../services/tag.service';
import { Tag } from '../../models/tag.model';
import { DataType } from '../../models/data-type.model';
import { DataService } from '../../services/data.service';
import * as _ from 'underscore';
import { UserService } from '../../services/user.service';
import { getRoleForDisplay, getRoleNameFromDisplay, Role } from '../../models/role.enum';
import { User } from '../../models/user.model';
import { PatientService } from '../../services/patient.service';
import { AuthService } from '../../services/auth.service';
import { Permissions, UserPermission } from '../../models/user-permission.model';
import { Adjudication } from '../../models/adjudication.model';

@Component({
  selector: 'lowy-edit-user',
  template: `
  <lowy-modal #modal>
    <div class="form">
      <div class="headline" *ngIf="userId">Edit User - {{usersName}}</div>
      <div class="headline" *ngIf="!userId">New User</div>
      <div class="seperator"></div>
      <form [formGroup]="editUserForm" (ngSubmit)="save()">
        <mat-form-field>
          <input matInput placeholder="Name" formControlName="name" type="text">
        </mat-form-field>
        <mat-form-field>
          <input matInput placeholder="Email" formControlName="email" type="text">
        </mat-form-field>
        <mat-form-field>
          <input matInput placeholder="Site Id" formControlName="siteId" type="text" (blur)="siteIdBlur()">
          <mat-hint class="mat-hint-error" *ngIf="siteIdErr">{{siteIdErr}}</mat-hint>
        </mat-form-field>
        <mat-form-field>
          <mat-select formControlName="role" placeholder="Role">
            <mat-option *ngFor="let role of roles" [value]="role">{{role}}</mat-option>
          </mat-select>
        </mat-form-field>

        <!-- TODO abstract all this out, add options up into the Permissions model, type everything -->

        <div *ngIf="editUserForm.value.role == 'Researcher'" class="row">
          <div class="col-6 permission-list">
            <p>Researcher Permissions:</p>
            <div class="row"><mat-checkbox [(ngModel)]="downloadRaw" [ngModelOptions]="{standalone: true}">Can download raw data</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="accessReportBuilder" [ngModelOptions]="{standalone: true}">Can access report builder</mat-checkbox></div>
          </div>
          <div class="col-6">
            <p>Can access images for:</p>
            <div class="row" *ngFor="let study of studyIds">
              <mat-checkbox [checked]="study.checked" [(ngModel)]="study.checked" [ngModelOptions]="{standalone: true}">{{study.name}}</mat-checkbox>
            </div>
          </div>
        </div>

        <div *ngIf="editUserForm.value.role == 'Grader'" class="row">
          <div class="col-6 permission-list">
            <p>Grader Permissions:</p>
            <div class="row"><mat-checkbox [(ngModel)]="searchData" [ngModelOptions]="{standalone: true}">Search Data</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="viewPatients" [ngModelOptions]="{standalone: true}">View Patients</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="viewPatientData" [ngModelOptions]="{standalone: true}">View Patient Data</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="downloadRaw" [ngModelOptions]="{standalone: true}">Can download raw data</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="managePendingApprovals" [ngModelOptions]="{standalone: true}">Manage Pending Approvals</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="uploadData" [ngModelOptions]="{standalone: true}">Upload Data</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="dataLock" [ngModelOptions]="{standalone: true}">Data Lock</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="srGrader" [ngModelOptions]="{standalone: true}">Sr Grader</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="clinicianReview" [ngModelOptions]="{standalone: true}">Clinician Review</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="adjudicate" [ngModelOptions]="{standalone: true}">Diagnose</mat-checkbox></div>
            <div class="row"><mat-checkbox [(ngModel)]="accessReportBuilder" [ngModelOptions]="{standalone: true}">Report Builder</mat-checkbox></div>
          </div>
          <div class="col-6">
            <p>Can access images for:</p>
            <div class="row" *ngFor="let study of studyIds">
              <mat-checkbox [checked]="study.checked" [(ngModel)]="study.checked" [ngModelOptions]="{standalone: true}">{{study.name}}</mat-checkbox>
            </div>
          </div>
        </div>

        <span class="input-error-message" *ngIf="errorMessage">{{errorMessage}}</span>
      </form>
      <div class="seperator"></div>
      <div class="button-row">
        <span (click)="cancel()">cancel</span>
        <span [ngClass]="{disabled: !editUserForm.valid}" (click)="save()">save</span>
      </div>
    </div>
  </lowy-modal>
  `,
  providers: [ TagService, DataService, PatientService, AuthService ]
})
export class EditUserModalComponent implements OnInit {
  public roles: string[];
  public siteIdErr: string;
  public errorMessage: string;
  public editUserForm: FormGroup;
  public studyIds: { name: string, checked: boolean }[];
  public userId: number;

  // permissions
  public downloadRaw;
  public accessReportBuilder;
  public viewPatients;
  public searchData;
  public viewPatientData;
  public managePendingApprovals;
  public uploadData;
  public dataLock;
  public srGrader;
  public clinicianReview;
  public adjudicate;

  @ViewChild('modal') modal: ModalComponent;

  @Output() updated: EventEmitter<null>;

  constructor(private formBuilder: FormBuilder,
    private userService: UserService,
    private patientService: PatientService,
    private authService: AuthService,
    private toastService: ToastService) {

      this.buildForm();
      this.updated = new EventEmitter();
  }

  async ngOnInit() {
    const roles = Object.keys(Role).filter(String);
    this.roles = [];
    // remove the ones we never make...
    _.each(roles, role => {
      const roleForDisplay = getRoleForDisplay(role);
      if (roleForDisplay) {
        this.roles.push(roleForDisplay);
      }
    });
    this.roles = _.sortBy(this.roles);
    const studyIds = await this.patientService.getAllStudyIds();
    this.studyIds = [];
    _.each(studyIds, studyId => {
      this.studyIds.push({ name: studyId, checked: false });
    });
  }

  public async open(user?: User) {
    // reset vals
    this.downloadRaw = false;
    this.accessReportBuilder = false;
    this.viewPatients = false;
    this.searchData = false;
    this.viewPatientData = false;
    this.managePendingApprovals = false;
    this.uploadData = false;
    this.dataLock = false;
    this.srGrader = false;
    this.clinicianReview = false;
    this.adjudicate = false;

    if (user) {
      this.userId = user.userId;
      this.editUserForm.setValue({
        name: user.name,
        email: user.email,
        role: getRoleForDisplay(user.role),
        siteId: user.siteId
      });
      // check and add in individual permissions...
      const permissions = await this.authService.getUserPermissions(this.userId);
      const studyIdsToAllow = [];
      _.each(permissions, permission => {
        switch (permission.permissionType) {
          case Permissions.allPermissionTypes.accessReportBuilder:
            this.accessReportBuilder = true;
            break;
          case Permissions.allPermissionTypes.downloadData:
            this.downloadRaw = true;
            break;
          case Permissions.allPermissionTypes.viewPatients:
            this.viewPatients = true;
            break;
          case Permissions.allPermissionTypes.searchData:
            this.searchData = true;
            break;
          case Permissions.allPermissionTypes.viewPatientData:
            this.viewPatientData = true;
            break;
          case Permissions.allPermissionTypes.managePendingApprovals:
            this.managePendingApprovals = true;
            break;
          case Permissions.allPermissionTypes.uploadData:
            this.uploadData = true;
            break;
          case Permissions.allPermissionTypes.dataLock:
            this.dataLock = true;
            break;
          case Permissions.allPermissionTypes.srGrader:
            this.srGrader = true;
            break;
          case Permissions.allPermissionTypes.clinicianReview:
            this.clinicianReview = true;
            break;
          case Permissions.allPermissionTypes.adjudicate:
            this.adjudicate = true;
            break;

          case Permissions.allPermissionTypes.accessRestrictedImages:
            studyIdsToAllow.push(permission.value);
            break;
        }
      });
      for (let i = 0; i < this.studyIds.length; i++) {
        if (studyIdsToAllow.includes(this.studyIds[i].name)) {
          this.studyIds[i].checked = true;
        }
      }
    }
    this.modal.open();
  }

  async buildForm() {
    this.editUserForm = this.formBuilder.group({
      name: ['', Validators.required],
      email: ['', Validators.compose([Validators.required, Validators.email])],
      role: ['', Validators.required],
      siteId: []
    });
    this.downloadRaw = null;
    this.accessReportBuilder = null;
    if (this.studyIds) {
      for (let i = 0; i < this.studyIds.length; i++) {
        this.studyIds[i].checked = false;
      }
    }
  }

  async save() {
    this.errorMessage = null;
    try {
      // build permissions
      this.editUserForm.patchValue({ role: getRoleNameFromDisplay(this.editUserForm.value.role) });
      const userPermissions: UserPermission[] = [];
      if ([Role.researcher, Role.grader].includes(this.editUserForm.value.role)) {
        if (this.downloadRaw) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.downloadData })
        }
        if (this.accessReportBuilder) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.accessReportBuilder })
        }

        // all remaining permissions here
        if (this.viewPatients) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.viewPatients })
        }
        if (this.searchData) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.searchData })
        }
        if (this.viewPatientData) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.viewPatientData })
        }
        if (this.managePendingApprovals) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.managePendingApprovals })
        }
        if (this.uploadData) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.uploadData })
        }
        if (this.dataLock) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.dataLock })
        }
        if (this.srGrader) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.srGrader })
        }
        if (this.clinicianReview) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.clinicianReview })
        }
        if (this.adjudicate) {
          userPermissions.push({ permissionType: Permissions.allPermissionTypes.adjudicate })
        }

        _.each(this.studyIds, studyId => {
          if (studyId.checked) {
            userPermissions.push({
              permissionType: Permissions.allPermissionTypes.accessRestrictedImages,
              value: studyId.name
            });
          }
        });
      }

      if (this.userId) {
        const userToUpdate = this.editUserForm.value;
        userToUpdate.permissions = userPermissions;
        await this.userService.updateUser(this.userId, userToUpdate);
      }
      else {
        const userToRegister: any = {
          name: this.editUserForm.value.name,
          email: this.editUserForm.value.email,
          role: this.editUserForm.value.role
        };
        if (this.editUserForm.value.siteId && this.editUserForm.value.siteId !== '') {
          userToRegister.siteId = this.editUserForm.value.siteId;
        }
        userToRegister.permissions = userPermissions;
        await this.authService.regsiter(userToRegister);
      }
      this.updated.emit();
      this.cancel();
      this.toastService.throwToast('User saved successfully');
    }
    catch (err) {
      this.errorMessage = err.message;
      this.cancel();
      this.toastService.throwToast('Error saving user: ' + err);
    }
  }

  cancel() {
    this.modal.close();
    this.buildForm();
    this.userId = null;
  }

  public async siteIdBlur() {
    this.siteIdErr = null;
    if (!this.siteId || this.siteId === '') { } // allow empty
    else {
      try {
        // visitId and siteId are 3 digits with leading zeroes
        const siteId = await this.patientService.testAndNormalizeVisitId(this.siteId);
        this.editUserForm.patchValue({
          siteId: siteId
        });
      }
      catch (err) {
        this.siteIdErr = err.message || 'Invalid Site ID';
      }
    }
  }

  get usersName() { return this.editUserForm.get('name').value; }
  get siteId() { return this.editUserForm.get('siteId').value; }
}



// WEBPACK FOOTER //
// ./src/app/home/manage-users/edit-user-modal.component.ts